// Dates on screen: the leaf (a tear-off calendar page, the category's hue on
// the month), the row, and the sheet that adds or edits one. Used by the
// Dates view, Today, Upcoming and the Calendar.
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  CalendarHeart,
  CalendarClock,
  Cake,
  ChevronRight,
  Flag,
  Heart,
  Hourglass,
  Receipt,
  Trash2,
  X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { DATE_CATEGORIES, type DateCategory, type DateSpec } from '@/kinds/date/schema'
import type { DateResource } from '@/kinds/date/apply'
import { CATEGORY_LABEL, CATEGORY_REPEAT, clashWith, clockAt, leadOf, rowLine, shortDay } from '@/lib/dates'
import { dateHue, tintText, tokens } from '@/lib/colour'
import { parseDate, todayLocal } from '@/lib/time'
import { useDates, usePeople, usePersonLinks, useSettings } from '@/db/hooks'
import { Avatar, PeoplePicker } from './PersonBits'
import { firstOf, nameOf, togetherLine } from '@/lib/people'
import type { PersonResource } from '@/kinds/person/apply'
import { mutate } from '@/runtime'
import { Button, Field, Segmented, Sheet, cx } from './ui'
import { ActionBar } from '@/components/ActionBar'
import { useShareWithItem } from '@/components/ShareWith'

export const CATEGORY_ICON: Record<DateCategory, LucideIcon> = {
  birthday: Cake,
  anniversary: Heart,
  appointment: CalendarClock,
  deadline: Flag,
  bill: Receipt,
  countdown: Hourglass,
  other: CalendarHeart,
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** A calendar leaf: the month in the category's hue over the day's number. */
export const DateLeaf = ({
  on,
  category,
  small,
}: {
  on: string
  category: DateCategory
  small?: boolean
}) => {
  const d = parseDate(on)
  const hue = dateHue(category)
  return (
    <span
      className={cx(
        'shrink-0 flex flex-col items-center justify-center rounded-[var(--radius-card-inner)] border border-line bg-raised tabular-nums leading-none',
        small ? 'w-9 h-9' : 'w-11 h-11',
      )}
      aria-hidden
    >
      <span
        className={cx('font-semibold', small ? 'text-[9px]' : 'text-[10px]')}
        style={{ color: tintText(hue) }}
      >
        {MONTHS[d.getMonth()]}
      </span>
      <span className={cx('font-semibold', small ? 'text-[15px] mt-0.5' : 'text-lg mt-0.5')}>
        {d.getDate()}
      </span>
    </span>
  )
}

/**
 * A birthday or anniversary names itself from its people: "Keira’s birthday",
 * "My birthday", "Our anniversary" (the owner and one other).
 */
const autoTitle = (category: DateCategory, people: PersonResource[]): string => {
  if (category !== 'birthday' && category !== 'anniversary') return ''
  if (people.length === 1)
    return people[0]!.spec.me ? `My ${category}` : `${firstOf(people[0]!)}’s ${category}`
  if (category === 'anniversary' && people.length === 2 && people.some((p) => p.spec.me))
    return 'Our anniversary'
  return ''
}

/** A few overlapping avatars for the people a date is about. */
export const Avatars = ({ people, size = 26 }: { people: PersonResource[]; size?: number }) => (
  <span className="inline-flex shrink-0" aria-hidden>
    {people.slice(0, 3).map((p, i) => (
      <span
        key={p.id}
        className="rounded-full ring-2 ring-[var(--color-panel)]"
        style={{ marginLeft: i ? -size / 3 : 0 }}
      >
        <Avatar person={p} size={size} />
      </span>
    ))}
  </span>
)

/** One date in a group list: the leaf, the name, and when in words. */
export const DateRow = ({
  date,
  on,
  today = todayLocal(),
}: {
  date: DateResource
  on: string
  today?: string
}) => {
  const Icon = CATEGORY_ICON[date.spec.category]
  // whose it is, when it is someone's: their faces in place of the kind's icon
  const people = usePersonLinks().get(date.id) ?? []
  return (
    <li>
      <Link
        to="/dates/$id"
        params={{ id: date.id }}
        search={{ on } as never}
        className="flex items-center gap-3 px-3.5 min-h-14 py-2 hover:bg-raised/50"
        data-testid="date-row"
      >
        <DateLeaf on={on} category={date.spec.category} />
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] truncate">{date.spec.title}</span>
          <span className={cx('block text-xs', on === today ? 'text-accent-text' : 'text-faint')}>
            {rowLine(date.spec, on, today)}
          </span>
        </span>
        {people.length ? (
          <Avatars people={people} size={22} />
        ) : (
          <Icon size={16} className="shrink-0 text-faint" aria-hidden />
        )}
      </Link>
    </li>
  )
}

/** Travel times offered, in minutes. */
const TRAVEL = [5, 10, 15, 20, 30, 45, 60, 90, 120]

/** Lead choices offered in the sheet; "default" follows Settings for the category. */
const LEADS = [0, 1, 2, 3, 7, 14, 30]
const leadLabel = (n: number) =>
  n === 0
    ? 'Only on the day'
    : n === 1
      ? 'The day before'
      : n === 7
        ? 'A week before'
        : n === 14
          ? 'Two weeks before'
          : n === 30
            ? 'A month before'
            : `${n} days before`

type Draft = {
  title: string
  category: DateCategory
  date: string
  time: string
  repeat: NonNullable<DateSpec['repeat']>
  no_year: boolean
  /** '' follows the category */
  lead: string
  notes: string
  /** an appointment's end (HH:MM), place and travel minutes ('' for none) */
  end: string
  place: string
  travel: string
  until: string
  skip: string[]
}

/** HH:MM plus minutes, and the minutes between two HH:MM (next day when the end is earlier). */
const plus = (hhmm: string, minutes: number) => {
  const m = Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5)) + minutes
  return `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}
const between = (a: string, b: string) => {
  const d =
    Number(b.slice(0, 2)) * 60 + Number(b.slice(3, 5)) - (Number(a.slice(0, 2)) * 60 + Number(a.slice(3, 5)))
  return d > 0 ? d : d + 1440
}

const draftOf = (r?: DateResource, category: DateCategory = 'birthday'): Draft =>
  r
    ? {
        title: r.spec.title,
        category: r.spec.category,
        date: r.spec.date,
        time: r.spec.time ?? '',
        repeat: r.spec.repeat ?? 'none',
        no_year: !!r.spec.no_year,
        lead: r.spec.lead_days === undefined ? '' : String(r.spec.lead_days),
        notes: r.spec.notes ?? '',
        end: r.spec.time && r.spec.minutes ? plus(r.spec.time, r.spec.minutes) : '',
        place: r.spec.place ?? '',
        travel: r.spec.travel_minutes ? String(r.spec.travel_minutes) : '',
        until: r.spec.until ?? '',
        skip: r.spec.skip ?? [],
      }
    : {
        title: '',
        category,
        date: '',
        time: '',
        repeat: CATEGORY_REPEAT[category],
        no_year: false,
        lead: '',
        notes: '',
        end: '',
        place: '',
        travel: '',
        until: '',
        skip: [],
      }

const PLACEHOLDER: Record<DateCategory, string> = {
  birthday: 'Sam’s birthday',
  anniversary: 'Our anniversary',
  appointment: 'Dentist',
  deadline: 'Tax return',
  bill: 'Car insurance',
  countdown: 'Japan',
  other: 'What to remember',
}

/** Add or edit a date. Everything is a spec patch; deleting goes to the Trash. */
export const DateSheet = ({
  resource,
  personId,
  on,
  onDone,
}: {
  resource?: DateResource
  /** a new date for this person (from their page) */
  personId?: string
  /** the occurrence it was opened from (a day in the calendar), which can be skipped */
  on?: string
  onDone: () => void
}) => {
  const shareWith = useShareWithItem(
    resource
      ? { kind: 'Date', id: resource.id, title: resource.spec.title, when: shortDay(resource.spec.date) }
      : null,
  )
  const settings = useSettings()
  const people = usePeople()
  const links = usePersonLinks()
  const linked = resource ? (links.get(resource.id) ?? []).map((p) => p.id) : []
  // who it is about: several people, the owner among them when their Person is "me"
  // untouched, it follows the links as they load (a sheet opened on a fresh page
  // renders before them); once the owner picks, their choice stands
  const [picked, setWho] = useState<string[] | null>(null)
  const who = picked ?? (resource ? linked : personId ? [personId] : [])
  const [picking, setPicking] = useState(false)
  const chosen = who.map((id) => people.find((p) => p.id === id)).filter((p) => !!p)
  const [d, setD] = useState<Draft>(() => draftOf(resource))
  const [repeatTouched, setRepeatTouched] = useState(!!resource)
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((cur) => ({ ...cur, [k]: v }))
  const categoryLead = leadOf({ category: d.category }, settings.date_leads)
  const yearly = d.repeat === 'yearly'

  const save = async () => {
    setError(null)
    // "Sam's birthday" and "Our anniversary" say themselves once the people are chosen
    const title = d.title.trim() || autoTitle(d.category, chosen)
    if (!title) return setError('Give it a name')
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date)) return setError('Pick the day')
    const spec: Record<string, unknown> = {
      title,
      category: d.category,
      date: d.date,
      repeat: d.repeat,
      time: d.time || undefined,
      no_year: yearly && d.no_year ? true : undefined,
      lead_days: d.lead === '' ? undefined : Number(d.lead),
      notes: d.notes.trim() || undefined,
      // an appointment's shape only means something at a time
      minutes: d.time && d.end ? between(d.time, d.end) : undefined,
      place: d.time && d.place.trim() ? d.place.trim() : undefined,
      travel_minutes: d.time && d.travel ? Number(d.travel) : undefined,
      until: d.repeat !== 'none' && d.until ? d.until : undefined,
      skip: d.repeat !== 'none' && d.skip.length ? d.skip : undefined,
    }
    try {
      if (resource) {
        // a field cleared is patched to null so it goes, rather than left as it was
        const patch: Record<string, unknown> = {}
        const was = resource.spec as Record<string, unknown>
        for (const [k, v] of Object.entries(spec))
          if (JSON.stringify(was[k]) !== JSON.stringify(v)) patch[k] = v === undefined ? null : v
        await mutate.grouped(async () => {
          if (Object.keys(patch).length) await mutate.patch(resource.id, { spec: patch })
          for (const id of linked)
            if (!who.includes(id)) await mutate.setEdge(resource.id, id, 'references', true)
          for (const id of who) if (!linked.includes(id)) await mutate.setEdge(resource.id, id, 'references')
        })
      } else {
        await mutate.grouped(async () => {
          const r = await mutate.create(
            'Date',
            Object.fromEntries(Object.entries(spec).filter(([, v]) => v !== undefined)),
          )
          for (const id of who) await mutate.setEdge(r.id, id, 'references')
        })
      }
      onDone()
    } catch (e) {
      setError((e as Error).message)
    }
  }

  return (
    <Sheet
      open
      onClose={onDone}
      title={resource ? 'Edit date' : 'Add a date'}
      footer={
        <>
          {resource && (
            <Button
              variant="quiet"
              className="mr-auto"
              onClick={() => void mutate.remove(resource.id).then(onDone)}
              data-testid="date-delete"
            >
              <Trash2 size={15} /> Delete
            </Button>
          )}
          <Button onClick={onDone}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()} data-testid="date-save">
            Save
          </Button>
        </>
      }
    >
      <div
        className="flex flex-wrap gap-1.5 mb-4"
        role="radiogroup"
        aria-label="Kind of date"
        data-testid="date-categories"
      >
        {DATE_CATEGORIES.map((c) => {
          const Icon = CATEGORY_ICON[c]
          const on = d.category === c
          const t = tokens(dateHue(c))
          return (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() =>
                setD((cur) => ({
                  ...cur,
                  category: c,
                  repeat: repeatTouched ? cur.repeat : CATEGORY_REPEAT[c],
                }))
              }
              className={cx(
                'inline-flex items-center gap-1.5 rounded-full min-h-9 px-3 text-[13px] border transition-colors',
                on ? 'border-transparent' : 'border-line text-muted hover:text-fg',
              )}
              style={on ? { background: t.soft, color: tintText(dateHue(c)) } : undefined}
              data-testid={`date-cat-${c}`}
            >
              <Icon size={14} aria-hidden /> {CATEGORY_LABEL[c]}
            </button>
          )
        })}
      </div>
      <div className="mb-3">
        <span className="block text-[13px] font-semibold text-muted mb-1.5">Who</span>
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="w-full flex items-center gap-3 rounded-[var(--radius-control)] border border-line bg-raised px-3 min-h-11 text-left"
          data-testid="date-who"
        >
          {chosen.length ? (
            <>
              <Avatars people={chosen} />
              <span className="flex-1 truncate text-[15px]">
                {chosen.length === 1 ? nameOf(chosen[0]!) : togetherLine(chosen)}
              </span>
            </>
          ) : (
            <span className="flex-1 text-[15px] text-faint">Nobody in particular</span>
          )}
          <ChevronRight size={16} className="text-faint" aria-hidden />
        </button>
      </div>
      {picking && <PeoplePicker chosen={who} onChange={setWho} onClose={() => setPicking(false)} />}
      <Field label="Name">
        <input
          value={d.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder={autoTitle(d.category, chosen) || PLACEHOLDER[d.category]}
          className="w-full"
          autoFocus={!resource}
          data-testid="date-title"
        />
      </Field>
      <div className="grid grid-cols-[1fr_auto] gap-3">
        <Field label={yearly && !d.no_year ? 'Date, with the year it began' : 'Date'}>
          <input
            type="date"
            value={d.date}
            onChange={(e) => set('date', e.target.value)}
            className="w-full"
            data-testid="date-date"
          />
        </Field>
        <Field label="At">
          <input
            type="time"
            value={d.time}
            onChange={(e) => set('time', e.target.value)}
            className="w-28"
            aria-label="Time, optional"
            data-testid="date-time"
          />
        </Field>
      </div>
      {d.time && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ends">
              <input
                type="time"
                value={d.end}
                onChange={(e) => set('end', e.target.value)}
                className="w-full"
                aria-label="Ends, optional"
                data-testid="date-end"
              />
            </Field>
            <Field label="Getting there">
              <select
                value={d.travel}
                onChange={(e) => set('travel', e.target.value)}
                className="w-full"
                data-testid="date-travel"
              >
                <option value="">Nowhere to go</option>
                {TRAVEL.map((m) => (
                  <option key={m} value={String(m)}>
                    {m < 60 ? `${m} min` : m === 60 ? '1 hour' : `${m / 60} hours`}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field
            label="Where"
            hint={d.travel ? 'You’ll get “get ready” and “leave now” on the day.' : undefined}
          >
            <input
              value={d.place}
              onChange={(e) => set('place', e.target.value)}
              placeholder="Castle Street Dental"
              className="w-full"
              data-testid="date-place"
            />
          </Field>
        </>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Segmented
          label="Repeats"
          value={d.repeat}
          options={[
            { value: 'none', label: 'Once' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'fortnightly', label: '2 weeks' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'yearly', label: 'Yearly' },
          ]}
          onChange={(v) => {
            setRepeatTouched(true)
            set('repeat', v)
          }}
        />
        {yearly && (
          <label className="inline-flex items-center gap-2 text-sm text-muted">
            <input
              type="checkbox"
              checked={d.no_year}
              onChange={(e) => set('no_year', e.target.checked)}
              data-testid="date-no-year"
            />
            Year not known
          </label>
        )}
      </div>
      {d.repeat !== 'none' && d.repeat !== 'yearly' && (
        <Field label="Last time" hint="Leave empty to keep going.">
          <input
            type="date"
            value={d.until}
            min={d.date || undefined}
            onChange={(e) => set('until', e.target.value)}
            className="w-full"
            data-testid="date-until"
          />
        </Field>
      )}
      {d.repeat !== 'none' && (on || d.skip.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="date-skips">
          {on && !d.skip.includes(on) && (
            <Button
              variant="chip"
              onClick={() => set('skip', [...d.skip, on].sort())}
              data-testid="date-skip"
            >
              Skip {shortDay(on)}
            </Button>
          )}
          {d.skip.map((x) => (
            <Button
              key={x}
              variant="chipOn"
              onClick={() =>
                set(
                  'skip',
                  d.skip.filter((y) => y !== x),
                )
              }
              aria-label={`Put ${shortDay(x)} back`}
            >
              {shortDay(x)} off <X size={12} />
            </Button>
          ))}
        </div>
      )}
      <Field label="Remind me" hint="And again on the day, at its time or with the morning brief.">
        <select
          value={d.lead}
          onChange={(e) => set('lead', e.target.value)}
          className="w-full"
          data-testid="date-lead"
        >
          <option value="">
            {leadLabel(categoryLead)} (the default for {CATEGORY_LABEL[d.category].toLowerCase()})
          </option>
          {LEADS.map((n) => (
            <option key={n} value={String(n)}>
              {leadLabel(n)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Notes">
        <textarea
          value={d.notes}
          onChange={(e) => set('notes', e.target.value)}
          rows={2}
          className="w-full"
          placeholder="Gift ideas, the address, the reference number"
          data-testid="date-notes"
        />
      </Field>
      {resource && (
        <ActionBar className="mb-3" items={[shareWith]} label="Date actions" testid="date-actions" />
      )}
      {error && <p className="text-sm text-rose-300 mb-3">{error}</p>}
    </Sheet>
  )
}

/**
 * A quiet note under a task's time when it falls inside an appointment:
 * "That's during Dentist, 14:00 to 14:45." Information, never a block.
 */
export const ClashNote = ({ day, time, minutes }: { day?: string; time?: string; minutes?: number }) => {
  const dates = useDates()
  const f = day && time ? clashWith(dates, day, time, minutes ?? 0) : undefined
  if (!f) return null
  return (
    <p className="flex items-center gap-1.5 text-xs text-muted -mt-2 mb-3" data-testid="clash-note">
      <CalendarClock size={13} aria-hidden />
      That’s during {f.date.spec.title}, {clockAt(f.start)} to {clockAt(f.end)}.
    </p>
  )
}
